import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, HelpCircle, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { Loading, Crumbs, StatusBadge, TypeTag, fmtMoney, ShareMenu } from "@/components/common";

export default function InvestigationDetail() {
  const { id } = useParams();
  const [iv, setIv] = useState(null);
  useEffect(() => { api.get(`/investigations/${id}`).then((r) => setIv(r.data)).catch(() => setIv(false)); }, [id]);

  if (iv === null) return <Loading />;
  if (iv === false) return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-slate-400">Investigación no encontrada.</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="investigation-detail">
      <Crumbs items={[{ label: "Fiscalización", to: "/fiscalizacion" }, { label: iv.title }]} />
      <div className="flex justify-end -mt-2 mb-2"><ShareMenu title={iv.title} /></div>
      <div className="flex items-center gap-2 mb-3"><TypeTag type={iv.content_type} /><StatusBadge status={iv.investigation_status} /></div>
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mb-4">{iv.title}</h1>
      <p className="text-lg text-slate-700 leading-relaxed mb-6">{iv.summary}</p>
      {iv.body && <p className="text-slate-600 leading-relaxed mb-8">{iv.body}</p>}

      {iv.amount != null && (
        <div className="bg-rose-50 border border-rose-100 rounded-lg p-5 mb-8">
          <p className="text-xs uppercase tracking-wide text-rose-500 font-semibold">Dinero relacionado</p>
          <p className="font-heading font-black text-3xl text-rose-600 mt-1">{fmtMoney(iv.amount)}</p>
        </div>
      )}

      {iv.entities_involved?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Personas y entidades involucradas</h3>
          <div className="flex flex-wrap gap-2">
            {iv.entities_involved.map((e) => <span key={e} className="bg-slate-100 text-slate-700 text-sm px-3 py-1.5 rounded-full">{e}</span>)}
          </div>
        </div>
      )}

      {iv.timeline?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Línea de tiempo</h3>
          <div className="border-l-2 border-cyan-200 pl-5 space-y-5">
            {iv.timeline.map((t) => (
              <div key={`${t.date}-${t.event}`} className="relative">
                <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-cyan-500 border-2 border-white" />
                <p className="text-sm font-semibold text-slate-900">{t.date}</p>
                <p className="text-sm text-slate-600">{t.event}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {iv.open_questions?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Preguntas pendientes</h3>
          <ul className="space-y-2">
            {iv.open_questions.map((q) => (
              <li key={q} className="flex gap-2 text-slate-700 text-sm"><HelpCircle className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" /> {q}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-8 bg-slate-100 rounded-lg p-5">
        <h3 className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900 mb-2"><MessageSquare className="w-4 h-4" /> Respuesta oficial</h3>
        <p className="text-slate-600 text-sm">{iv.official_response || "Aún no se ha recibido respuesta oficial. Se otorga derecho a réplica."}</p>
      </div>

      {iv.sources?.length > 0 && (
        <div>
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Fuentes y documentos</h3>
          {iv.sources.map((s) => (
            <a key={s} href={s} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cyan-600 hover:underline break-all mb-1">
              <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {s}
            </a>
          ))}
        </div>
      )}
      {iv.last_reviewed && <p className="text-xs text-slate-400 mt-8">Última revisión: {new Date(iv.last_reviewed).toLocaleDateString("es-PR")}</p>}
    </div>
  );
}
